/**
 * LAS CLAVES QUE VIAJAN EN `type_details`.
 *
 * Vive fuera de `api.js` desde el 2026-09-13 porque ahora la miran dos: el que
 * LEE una póliza del servidor (`api.js`) y el que la ESCRIBE
 * (`polizaParaElServidor.js`). Con una copia en cada uno, el día que alguien
 * añada un campo a una sola de las dos, se guardaría y volvería vacío al
 * recargar — que es exactamente la regla de oro 139.
 *
 * ES UNA LISTA BLANCA ESCRITA A MANO, y esa es su gracia y su peligro: nada
 * llega a `type_details` si no está aquí.
 */
export const POLICY_DETAIL_KEYS = [
  'familySize', 'membersActive', 'agentName', 'producerCode', 'coverageType', 'liabilityCoverage',
  // Health/ACA application number (Marketplace) — shown on the card next to Member ID.
  'applicationNumber',
  // Nombre del Marketplace por el que se inscribió (HealthCare.gov o el estatal).
  'marketplaceName',
  // Health/ACA optional money fields shown next to Monthly Premium.
  'subsidy', 'planTotalCost',
  // El SUBTIPO de una poliza Suplementaria (accidente, cancer, hospital...).
  // Lissette 2026-09-08. Texto libre a proposito: ver crm-app/tiposDeSuplemento.js.
  'supplementType',
  'propertyAddress', 'propertyType', 'yearBuilt', 'squareFootage', 'insuredValue', 'dwellingCoverage',
  'vehicleYear', 'vehicleMake', 'vehicleModel', 'vin', 'licensePlate', 'driversOnPolicy',
  // Varios carros en una póliza de auto (Lissette 2026-10-12): la lista entera.
  // El primero se espeja en los campos de arriba. Ver crm-app/vehiculosDePoliza.js.
  'vehiculos',
  'businessName', 'businessType', 'businessAddress', 'numEmployees', 'annualRevenue',
  'paymentType', 'paymentMethod', 'cardLast4', 'cardExpMonth', 'cardExpYear', 'cardBrand', 'bankLast4', 'payerName', 'billingDay',
  // Commission + analyst (shown on every policy card, cross-type).
  // commissionMonthly = per-month commission; the by-policy table multiplies it
  // by the term (effective→renewal) to derive commissionAmount (Lissette 2026-06-22).
  'commissionPercent', 'commissionAmount', 'commissionMonthly', 'analystName',
  // COMISIÓN POR MIEMBRO (Lissette 2026-09-11): en salud la aseguradora paga
  // por persona en la póliza, así que la tabla de Comisiones por Póliza guarda
  // cuánto se cobra por cada una y por qué es ese porcentaje (split / agencia).
  // TIENEN QUE ESTAR EN ESTA LISTA: es una lista blanca escrita a mano, y un
  // campo que falte aquí se guarda desde la pantalla, el servidor contesta 200
  // y vuelve vacío al recargar, sin un solo error (regla 139).
  'comisionPorPersona', 'razonDeComision',
  // % DE LA PRIMA y MESES DE ADELANTO (Lissette 2026-10-11): Vida y las
  // polizas que no son de salud se pagan por porcentaje de la prima; Vida,
  // ademas, con un adelanto de N meses. Ver crm-app/comisionPorTipo.js.
  'porcentajeDePrima', 'mesesDeAdelanto',
  // Sale date — when the policy was actually sold (drives the Sales Report).
  'saleDate',
  // Why a policy was cancelled — required when status is set to "cancelled",
  // shown on the cancelled policy card (Lissette 2026-06-26).
  'cancelReason',
  // Life extras — mirror the carrier PolicySummary columns (Lissette 2026-07-02).
  'policyOwnerName', 'termDuration', 'issueDate', 'premiumDueDate',
  'billablePremium', 'billingFrequency', 'billingMethod', 'agentNumber', 'agencyNumber',
  // Medicare extras (Lissette 2026-08-09, "proyecto grande"): las columnas que
  // pidió la agente en el Excel + lo que exige el checklist de inscripción
  // (MBI y las fechas de Parte A/B). Van en type_details y NO en columnas
  // propias porque solo aplican a MA / Suplemento / Parte D — el mismo criterio
  // que se usó con los extras de Vida.
  //
  // partBDate es el dato con más peso del grupo: la fecha efectiva de la Parte B
  // arranca el reloj de inscripción abierta de Medigap, y perderla le cuesta
  // dinero al cliente. initialPremium/currentPremium van separados a propósito:
  // la prima cambia cada año y la agente necesita ver ambas para explicar el
  // salto sin abrir el historial.
  'medicareNumber', 'partADate', 'partBDate', 'companyId', 'planYear',
  'initialPremium', 'currentPremium', 'enrollmentCode',
  // SOA y HRA (Lissette 2026-09-13): los dos tramites que acompanan una venta
  // de Medicare. Ver crm-app/camposDeMedicare.js para que son y por que el
  // desplegable tambien deja escribir. TIENEN QUE ESTAR EN ESTA LISTA: es una
  // lista blanca a mano y un campo que falte aqui se guarda desde la pantalla,
  // el servidor contesta 200 y vuelve vacio al recargar (regla 139).
  'soaStatus', 'soaSigned', 'hraStatus',
  // La categoria de Medicaid, la FECHA en que se firmo el SOA y el nivel de LIS
  // (Lissette 2026-09-14). La primera decide quien paga la prima y los copagos
  // del cliente; el LIS, cuanto le subsidian las medicinas. Misma lista blanca,
  // misma trampa: si faltan aqui, se guardan y vuelven vacios sin un error.
  'medicaidEligibility', 'soaSignedDate', 'lisLevel',
  // Ficha de Medicare Advantage rehecha (Lissette 2026-10-01, con su dibujo de
  // dos columnas): los seis datos que pidió y no existían. Tipo de producto es
  // texto libre que se va llenando con lo ya usado (HMO, PPO, D-SNP...).
  // Misma lista blanca, misma trampa (regla 139).
  'productType', 'givebackAmount', 'partBPremium', 'contractPbpSegment', 'medicaidNumber', 'subAgent',
  // Vida — segunda tanda (Lissette 2026-08-09, "proyecto grande"). La primera
  // (julio) copió las columnas del PolicySummary de la aseguradora; estas son
  // las que faltaban del Excel de la agente, y casi todas son del CICLO DE VIDA
  // de la solicitud, no de la póliza en sí: aprobada -> papeles enviados ->
  // entregada -> colocada (o sea, pagada). Sin ellas la agente no sabe en qué
  // punto está un caso sin llamar a la compañía.
  //
  // riskClass sirve también a anualidades / incapacidad / cuidado a largo plazo,
  // que en el Excel piden "risk class" con el mismo sentido.
  'approvedDate', 'deliveryPapersDate', 'deliveryDate', 'placingDate',
  'beneficiaryContingent', 'conversionAge', 'riskClass',
  'commissionPaid', 'commissionSplit', 'ownerAddress', 'verificationForm',
  // ACA. De la tanda del 2026-08-09 solo queda en pantalla el período de
  // inscripción (OE/SEP), que Lissette subió junto al # de aplicación el
  // 2026-08-10 y que además alimenta el reporte "pólizas por período".
  //
  // `marketplaceId`, `netPremium` y `documentsPending` se retiraron de la ficha
  // y del importador a petición suya, pero SIGUEN AQUÍ a propósito: si alguna
  // póliza alcanzó a guardarlos, quitarlos del mapeo los borraría en el
  // siguiente guardado. Sin campo en el formulario nadie los escribe, así que
  // no estorban; y si algún día se piden de vuelta, el dato sigue ahí.
  'enrollmentPeriod', 'marketplaceId', 'netPremium', 'documentsPending',
  // P&C (Lissette 2026-08-09): en propiedad y casualidad la prima casi nunca es
  // mensual — se cotiza anual o semestral, y la agente necesita las dos a la
  // vista para comparar. `generalAgent` es el general agent / MGA por el que se
  // colocó el negocio, distinto del agente que lo vendió.
  'premiumAnnual', 'premiumSemiAnnual', 'generalAgent',
  // Cuál de las tres primas se enseña y cuenta para la comisión (Lissette
  // 2026-10-22): 'monthly' | 'semiannual' | 'annual'. Ver primaDePoliza.js.
  'premiumDefault',
  // Dental, Visión, Mascotas y Gastos Finales (Lissette 2026-08-09). Estos NO
  // salen del Excel sino de los checklists de solicitud, de la sección "Plan
  // Terms to Review with Client": son las condiciones que la agente repasa con
  // el cliente y que después nadie recuerda cuando llama a reclamar.
  //
  // Se REUTILIZA todo lo que ya existía en vez de duplicarlo:
  //   deducible        -> `deductible` (columna, ya estaba)
  //   clase de riesgo  -> `riskClass` (se añadió con Vida; Gastos Finales ES vida)
  //   fecha de débito  -> `billingDay` (ya estaba, en Información de Pago)
  // Y tres claves se comparten entre ramos porque significan lo mismo aunque el
  // checklist las llame distinto: `annualMaximum` es el máximo anual del dental
  // y el límite anual de mascotas; `waitingPeriod` cubre los tres períodos de
  // espera (dental, mascotas y el beneficio graduado de gastos finales);
  // `networkType` sirve a dental (DPPO/DHMO) y a visión.
  'annualMaximum', 'waitingPeriod', 'networkType', 'coinsurance', 'missingToothClause',
  'examCopay', 'frameAllowance', 'contactsAllowance',
  'petName', 'petSpecies', 'petBreed', 'petAge', 'microchipNumber', 'reimbursementPercent',
  'funeralHomeAssignment',
  // Short Term Medical (Lissette 2026-08-26). `termDuration`, `deductible` y
  // `coinsurance` NO se repiten aqui: ya existian y significan lo mismo. Estas
  // cuatro son las que no tenian sitio en ningun otro ramo.
  'outOfPocketMax', 'benefitMax', 'renewable', 'preExistingExcluded',
  // Costos del plan de SALUD (Lissette 2026-10-08): cuánto paga el cliente en
  // el doctor primario y en la sala de emergencia. Texto libre ("$30", "20%
  // después del deducible"). El deducible y el máximo de bolsillo NO se
  // repiten: son `deductible` (columna) y `outOfPocketMax` (de Short Term).
  'pcpCopay', 'erCopay',
  // El especialista y la medicina genérica (Lissette 2026-10-13, para que la
  // entrevista del cotizador traiga el plan actual entero). Mismo texto libre.
  'specialistCopay', 'genericDrugCopay',
  // NO es un dato de la póliza: es la decisión de la agente sobre el detector
  // de duplicadas ("ya la revisé, esta se queda"). Vive aquí porque typeDetails
  // ya viaja en los dos sentidos y no hace falta ni columna ni migración, y
  // porque este mapeo es una lista blanca: ninguna pantalla pinta una clave que
  // no le pidan por su nombre, así que no se cuela en la ficha.
  // Guardarla en la póliza (y no en un ajuste de la agencia) es a propósito: si
  // la póliza se borra, la decisión se va con ella.
  'dupOk',
];
